import React, { useState } from "react";
import { Alert, Button, Modal, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import {
    createCheckoutSession,
    createCustomerPortalSession,
    getAppBillingCheckoutSource,
    getBillingErrorMessage,
} from "../../../Services/billing.service";
import "./lockTokens.css";
import "./CommercialGateModal.css";

const { Text } = Typography;

const CONTACT_EMAIL = "contact@verdictia.com";

const buildMailtoHref = (subject, body) =>
    `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const getPreferredDefault = (message, language, fieldBase) => {
    const lang = String(language || "en").toLowerCase();
    const localized = lang.startsWith("fr")
        ? message?.[`${fieldBase}_fr`]
        : message?.[`${fieldBase}_en`];
    return (
        localized ||
        message?.[fieldBase] ||
        message?.[`${fieldBase}_fr`] ||
        message?.[`${fieldBase}_en`] ||
        ""
    );
};

export const getCommercialDecisionText = (decision, t, language = "en") => {
    const message = decision?.message || {};
    const fallbackTitle =
        getPreferredDefault(message, language, "default_title") ||
        "Action indisponible";
    const fallbackBody =
        getPreferredDefault(message, language, "default_body") ||
        "Vos droits ou quotas ont changé. Veuillez réessayer.";
    const fallbackCta =
        getPreferredDefault(message, language, "default_cta_label") ||
        null;

    return {
        title: message.title_key
            ? t(message.title_key, { defaultValue: fallbackTitle })
            : fallbackTitle,
        body: message.body_key
            ? t(message.body_key, { defaultValue: fallbackBody })
            : fallbackBody,
        ctaLabel: message.cta_label_key
            ? t(message.cta_label_key, { defaultValue: fallbackCta || "" })
            : fallbackCta,
    };
};

export const isPartialAnalysisDecision = (decision) =>
    decision?.status === "requires_confirmation";

const getPageCounts = (decision) => {
    const metadata = decision?.metadata_json || decision?.metadata || {};
    return {
        analyzedPages: metadata.analyzed_pages_count ?? metadata.included_pages_count ?? null,
        excludedPages: metadata.excluded_pages_count ?? null,
        announcedPages: metadata.announced_page_count ?? null,
        detectedPages: metadata.detected_page_count ?? null,
        partialAnalysis: metadata.partial_analysis ?? null,
    };
};

const getCtaLabel = (target, ctaLabel, t) => {
    if (ctaLabel) return ctaLabel;
    if (target === "upgrade") {
        return t("commercial.cta.upgrade", { defaultValue: "Voir les options" });
    }
    if (target === "contact") {
        return t("commercial.cta.contact", { defaultValue: "Contacter l'équipe" });
    }
    if (target === "retry") {
        return t("commercial.cta.retry", { defaultValue: "Réessayer" });
    }
    return null;
};

const PLAN_RANK = {
    trial: 0,
    entrepreneur: 1,
    solo: 2,
    pro: 3,
    enterprise: 4,
};

const SELF_SERVE_UPGRADE_CODES = new Set(["entrepreneur", "solo", "pro"]);

export const normalizeCommercialPlanCode = (plan) => {
    const raw = typeof plan === "string"
        ? plan
        : plan?.code || plan?.plan_code || plan?.commercial_plan_code || plan?.name || "";
    const value = String(raw || "").trim().toLowerCase();

    if (!value) return "trial";
    if (value.includes("trial") || value.includes("essai")) return "trial";
    if (value.includes("entrepreneur")) return "entrepreneur";
    if (value.includes("solo")) return "solo";
    if (value.includes("enterprise") || value.includes("entreprise")) return "enterprise";
    if (value === "pro" || value.includes("professionnel")) return "pro";
    return Object.prototype.hasOwnProperty.call(PLAN_RANK, value) ? value : "trial";
};

const getDecisionMetadata = (decision = {}) =>
    decision?.metadata_json || decision?.metadata || {};

const isEntrepreneurRepeatPurchaseDecision = (decisionOrCapability, currentPlanCode) => {
    if (currentPlanCode !== "entrepreneur" || typeof decisionOrCapability === "string") {
        return false;
    }
    const metadata = getDecisionMetadata(decisionOrCapability);
    const decisionPlanCode = normalizeCommercialPlanCode(
        metadata.plan_code ||
        decisionOrCapability?.usage_snapshot?.plan_code ||
        decisionOrCapability?.usage_snapshot?.plan
    );
    return decisionPlanCode === "entrepreneur" && (
        metadata.repeat_purchase_plan_code === "entrepreneur" ||
        metadata.repeat_purchase_allowed === true
    );
};

const getUsageByTypeFromSnapshot = (usageSnapshot = {}) =>
    usageSnapshot?.usage || usageSnapshot?.usage_by_type || {};

const isConsumedEntrepreneurUsageSnapshot = (usageSnapshot, currentPlanCode) => {
    if (currentPlanCode !== "entrepreneur") return false;
    const usageByType = getUsageByTypeFromSnapshot(usageSnapshot);
    const completeAnalysis = usageByType.complete_analysis || usageSnapshot?.complete_analysis;
    if (!completeAnalysis || completeAnalysis.unlimited) return false;

    const limit = Number(completeAnalysis.limit);
    const used = Number(completeAnalysis.used || 0);
    const reserved = Number(completeAnalysis.reserved || 0);
    const remaining = Number(completeAnalysis.remaining);

    return Number.isFinite(limit) &&
        limit > 0 &&
        used >= limit &&
        reserved <= 0 &&
        Number.isFinite(remaining) &&
        remaining <= 0;
};

const withEntrepreneurRepeatPurchaseContext = (decision, currentPlanCode, usageSnapshot) => {
    if (
        isEntrepreneurRepeatPurchaseDecision(decision, currentPlanCode) ||
        !isConsumedEntrepreneurUsageSnapshot(usageSnapshot, currentPlanCode)
    ) {
        return decision;
    }

    const metadata = getDecisionMetadata(decision);
    return {
        ...(typeof decision === "object" && decision ? decision : {}),
        metadata_json: {
            ...metadata,
            plan_code: "entrepreneur",
            commercial_block_reason: metadata.commercial_block_reason || "entrepreneur_analysis_consumed",
            repeat_purchase_allowed: true,
            repeat_purchase_plan_code: "entrepreneur",
        },
    };
};

export const getBlockedCapabilityFromDecision = (decisionOrCapability) => {
    if (typeof decisionOrCapability === "string") return decisionOrCapability;

    const metadata = getDecisionMetadata(decisionOrCapability);
    const message = decisionOrCapability?.message || {};
    const combined = [
        metadata.feature,
        metadata.capability,
        metadata.capability_key,
        metadata.usage_type,
        metadata.action,
        metadata.reason,
        decisionOrCapability?.action,
        decisionOrCapability?.code,
        message.code,
    ]
        .filter(Boolean)
        .map((part) => String(part).toLowerCase())
        .join(" ");

    if (/canada|sovereign|souverain|security|securite|sécurité|dedicated|sso|advanced_repository|besoin[_ -]?custom|custom[_ -]?security|security[_ -]?custom/.test(combined)) {
        return "enterprise_only";
    }
    if (/workspace_user|user_limit|user_management|multi[_ -]?user|collaboration/.test(combined)) {
        return "collaboration";
    }
    if (/repository/.test(combined)) {
        return "shared_repository";
    }
    if (/monthly_quota|quota_exceeded|complete_analysis|comparison_limit|supporting_document_limit|risk_comparison|supporting_document/.test(combined)) {
        return "analysis_volume";
    }
    if (/trial_limit|trial_analysis|first_analysis/.test(combined)) {
        return "first_analysis";
    }
    if (/page_limit|page_count|document_length|document_too_long|max_pages/.test(combined)) {
        return "document_scope";
    }
    if (/criteria_prompt/.test(combined)) {
        return "pro_feature";
    }
    if (/criteria_limit|max_criteria|custom_criteria/.test(combined)) {
        return "analysis_volume";
    }
    if (/strategic_memo|standard_export|export|comment_details|contract_profile_advanced|feature_locked/.test(combined)) {
        return "self_serve_premium";
    }

    return "generic_upgrade";
};

const onlyAboveCurrentPlan = (currentPlanCode, planCodes) => {
    const currentRank = PLAN_RANK[currentPlanCode] ?? PLAN_RANK.trial;
    return planCodes.filter((code) => (PLAN_RANK[code] ?? -1) > currentRank);
};

export const getUpgradeOptions = (currentPlan, blockedCapability) => {
    const currentPlanCode = normalizeCommercialPlanCode(currentPlan);
    const capability = getBlockedCapabilityFromDecision(blockedCapability);

    if (currentPlanCode === "enterprise") return [];

    if (isEntrepreneurRepeatPurchaseDecision(blockedCapability, currentPlanCode)) {
        return ["entrepreneur", "solo", "pro"].map((planCode) => ({
            planCode,
            action: SELF_SERVE_UPGRADE_CODES.has(planCode) ? "checkout" : "contact",
            variant: planCode === "entrepreneur" ? "entrepreneur_repeat_purchase" : undefined,
        }));
    }

    let targetCodes;
    switch (capability) {
        case "enterprise_only":
            targetCodes = ["enterprise"];
            break;
        case "collaboration":
        case "shared_repository":
        case "pro_feature":
            targetCodes = ["pro"];
            break;
        case "analysis_volume":
            if (currentPlanCode === "trial") {
                targetCodes = ["entrepreneur", "solo", "pro"];
            } else if (currentPlanCode === "entrepreneur") {
                targetCodes = ["solo", "pro"];
            } else if (currentPlanCode === "solo") {
                targetCodes = ["pro"];
            } else if (currentPlanCode === "pro") {
                targetCodes = ["enterprise"];
            } else {
                targetCodes = ["solo", "pro"];
            }
            break;
        case "first_analysis":
        case "document_scope":
            if (currentPlanCode === "trial") {
                targetCodes = ["entrepreneur", "solo", "pro"];
            } else if (currentPlanCode === "entrepreneur") {
                targetCodes = ["solo"];
            } else if (currentPlanCode === "solo") {
                targetCodes = ["pro"];
            } else if (currentPlanCode === "pro") {
                targetCodes = ["enterprise"];
            } else {
                targetCodes = ["entrepreneur", "solo"];
            }
            break;
        case "self_serve_premium":
            if (currentPlanCode === "trial") {
                targetCodes = ["entrepreneur", "solo", "pro", "enterprise"];
            } else if (currentPlanCode === "entrepreneur") {
                targetCodes = ["solo", "pro", "enterprise"];
            } else if (currentPlanCode === "solo") {
                targetCodes = ["pro", "enterprise"];
            } else if (currentPlanCode === "pro") {
                targetCodes = [];
            } else {
                targetCodes = ["entrepreneur", "solo", "pro", "enterprise"];
            }
            break;
        default:
            if (currentPlanCode === "trial") {
                targetCodes = ["entrepreneur", "solo", "pro"];
            } else if (currentPlanCode === "entrepreneur") {
                targetCodes = ["solo", "pro"];
            } else if (currentPlanCode === "solo") {
                targetCodes = ["pro", "enterprise"];
            } else if (currentPlanCode === "pro") {
                targetCodes = [];
            } else {
                targetCodes = ["entrepreneur", "solo", "pro"];
            }
            break;
    }

    return onlyAboveCurrentPlan(currentPlanCode, targetCodes)
        .map((planCode) => ({
            planCode,
            action: SELF_SERVE_UPGRADE_CODES.has(planCode) ? "checkout" : "contact",
        }));
};

const PLAN_DEFS = [
    {
        code: "entrepreneur",
        featured: false,
        i18nBase: "commercial.plans.entrepreneur",
        defaults: {
            badge: "Ponctuel",
            name: "Entrepreneur",
            audience: "Contrat important sans abonnement",
            price: "99 CAD",
            period: "ponctuel",
            features: [
                "1 dossier ponctuel",
                "Contrats jusqu'à 25 pages",
                "Comparaisons et documents d'appui inclus selon quota",
                "Mémo stratégique, exports et détails complets inclus",
                "1 utilisateur inclus",
            ],
            cta: "Passer à Entrepreneur",
            note: "Accès ponctuel self-serve depuis votre workspace.",
        },
    },
    {
        code: "solo",
        featured: false,
        i18nBase: "commercial.plans.solo",
        defaults: {
            badge: "Individuel",
            name: "Solo",
            audience: "Entrepreneur, avocat solo, consultant ou responsable opérationnel",
            price: "249 CAD",
            period: "/ mois",
            features: [
                "15 dossiers de revue par période",
                "Contrats courts à moyens, jusqu'à 35 pages",
                "Profil du contrat complet",
                "Commentaires, mémo stratégique et export inclus",
                "1 utilisateur inclus",
            ],
            cta: "Démarrer",
            note: "Accès accompagné après qualification du besoin.",
        },
    },
    {
        code: "pro",
        featured: true,
        i18nBase: "commercial.plans.pro",
        defaults: {
            badge: "Équipe active",
            name: "Pro",
            audience: "Petite équipe juridique, ops, procurement ou cabinet léger",
            price: "899 CAD",
            period: "/ mois",
            features: [
                "50 dossiers de revue par période",
                "Contrats plus longs, jusqu'à 75 pages",
                "Travail en équipe, 4 utilisateurs inclus",
                "Référentiel documentaire standard",
                "Comparaisons et documents d'appui inclus",
            ],
            cta: "Démarrer",
            note: "Cadrage conseillé pour les équipes actives.",
        },
    },
    {
        code: "enterprise",
        featured: false,
        ctaType: "contact",
        i18nBase: "commercial.plans.enterprise",
        defaults: {
            badge: "Sur mesure",
            name: "Enterprise",
            audience: "Cabinet, direction juridique ou organisation avec volumes et gouvernance",
            price: "Sur devis",
            period: "",
            features: [
                "Volumes et limites sur mesure",
                "Contrats longs, annexes et workflows complexes",
                "Référentiel avancé ou dédié",
                "Gouvernance, sécurité et accompagnement",
            ],
            cta: "Me contacter",
            note: "Échange dédié pour cadrer volumes, sécurité et gouvernance.",
        },
    },
];

const ENTREPRENEUR_REPEAT_PURCHASE_PLAN_DEF = {
    code: "entrepreneur",
    featured: false,
    i18nBase: "commercial.plans.entrepreneurRepeat",
    defaults: {
        badge: "Ponctuel",
        name: "Nouveau dossier",
        audience: "Pour revoir un autre contrat sans abonnement.",
        price: "99 CAD",
        period: "",
        features: [
            "1 dossier de revue complet",
            "Contrat jusqu'à 25 pages",
            "Mémo stratégique et export inclus",
            "90 jours pour lancer la revue",
        ],
        cta: "Acheter un nouveau dossier",
        note: "Nouveau pack Entrepreneur pour un autre dossier de revue.",
    },
};

const PLAN_DEFS_BY_CODE = PLAN_DEFS.reduce((acc, plan) => ({
    ...acc,
    [plan.code]: plan,
}), {});

const FEATURE_INDEXES = [1, 2, 3, 4, 5];

const PlanCard = ({ plan, t, onCheckout, loadingPlanCode }) => {
    const { i18nBase, defaults } = plan;
    const features = FEATURE_INDEXES
        .map((index) => t(`${i18nBase}.feature${index}`, {
            defaultValue: defaults.features[index - 1] || "",
        }))
        .filter(Boolean);
    const note = t(`${i18nBase}.note`, { defaultValue: defaults.note });
    const period = t(`${i18nBase}.period`, { defaultValue: defaults.period });

    const planName = t(`${i18nBase}.name`, { defaultValue: defaults.name });
    const ctaHref = plan.ctaType === "contact"
        ? buildMailtoHref(
            t("commercial.plans.contactMail.subject", {
                defaultValue: "Verdictia — Échange plan Enterprise",
                plan: planName,
            }),
            t("commercial.plans.contactMail.body", {
                defaultValue: "Bonjour,\n\nJe souhaite échanger sur le plan Enterprise de Verdictia (volumes, sécurité, gouvernance).\n\nContexte : \nOrganisation : \nVolume estimé de contrats : \n\nMerci de me recontacter.\n",
                plan: planName,
            })
        )
        : null;
    const ctaLabel = plan.ctaLabel || t(`${i18nBase}.cta`, { defaultValue: defaults.cta });
    const isLoading = loadingPlanCode === plan.code;
    const isBusy = Boolean(loadingPlanCode);

    return (
        <article
            className={`commercial-gate-plan${plan.featured ? " commercial-gate-plan--featured" : ""}`}
            data-plan={plan.code}
        >
            <span className="commercial-gate-plan__badge">
                {t(`${i18nBase}.badge`, { defaultValue: defaults.badge })}
            </span>
            <h4 className="commercial-gate-plan__name">{planName}</h4>
            <p className="commercial-gate-plan__audience">
                {t(`${i18nBase}.audience`, { defaultValue: defaults.audience })}
            </p>
            <div className="commercial-gate-plan__price">
                <span className="commercial-gate-plan__price-amount">
                    {t(`${i18nBase}.price`, { defaultValue: defaults.price })}
                </span>
                {period && (
                    <span className="commercial-gate-plan__price-period">{period}</span>
                )}
            </div>
            <ul className="commercial-gate-plan__features">
                {features.map((feature) => (
                    <li key={feature}>{feature}</li>
                ))}
            </ul>
            {plan.ctaType === "contact" ? (
                <a
                    className="commercial-gate-plan__cta premium-lock-focus"
                    href={ctaHref}
                >
                    {ctaLabel}
                </a>
            ) : (
                <button
                    type="button"
                    className="commercial-gate-plan__cta premium-lock-focus"
                    onClick={() => onCheckout(plan.code)}
                    disabled={isBusy}
                    aria-busy={isLoading ? "true" : undefined}
                >
                    {isLoading
                        ? t("commercial.billing.redirecting", { defaultValue: "Ouverture..." })
                        : ctaLabel}
                </button>
            )}
            {note && <p className="commercial-gate-plan__note">{note}</p>}
        </article>
    );
};

const EnterpriseContactStrip = ({ plan, t }) => {
    const planName = t(`${plan.i18nBase}.name`, { defaultValue: plan.defaults.name });
    const ctaHref = buildMailtoHref(
        t("commercial.plans.contactMail.subject", {
            defaultValue: "Verdictia — Échange plan Enterprise",
            plan: planName,
        }),
        t("commercial.plans.contactMail.body", {
            defaultValue: "Bonjour,\n\nJe souhaite échanger sur le plan Enterprise de Verdictia (volumes, sécurité, gouvernance).\n\nContexte : \nOrganisation : \nVolume estimé de contrats : \n\nMerci de me recontacter.\n",
            plan: planName,
        })
    );

    return (
        <div className="commercial-gate-enterprise-strip">
            <div className="commercial-gate-enterprise-strip__copy">
                <span className="commercial-gate-enterprise-strip__kicker">
                    {t("commercial.upgradeOptions.enterpriseStrip.kicker", { defaultValue: "Sur mesure" })}
                </span>
                <strong>
                    {t("commercial.upgradeOptions.enterpriseStrip.title", { defaultValue: "Besoin d'un cadre Enterprise ?" })}
                </strong>
                <span>
                    {t("commercial.upgradeOptions.enterpriseStrip.body", {
                        defaultValue: "Volumes, sécurité ou gouvernance spécifique : échange dédié avec Verdictia.",
                    })}
                </span>
            </div>
            <a
                className="commercial-gate-enterprise-strip__cta premium-lock-focus"
                href={ctaHref}
            >
                {getUpgradeCtaLabel({ planCode: plan.code }, t)}
            </a>
        </div>
    );
};

const getUpgradeCtaLabel = (option, t) => {
    if (option.variant === "entrepreneur_repeat_purchase") {
        return t("commercial.upgradeOptions.cta.entrepreneurRepeat", {
            defaultValue: "Acheter un nouveau dossier",
        });
    }
    if (option.planCode === "enterprise") {
        return t("commercial.upgradeOptions.cta.enterprise", {
            defaultValue: "Nous contacter pour Enterprise",
        });
    }
    return t(`commercial.upgradeOptions.cta.${option.planCode}`, {
        defaultValue: {
            entrepreneur: "Passer à Entrepreneur",
            solo: "Passer à Solo",
            pro: "Passer à Pro",
        }[option.planCode] || "Débloquer cette action",
    });
};

const ContextualLimitAction = ({
    currentPlanCode,
    t,
    canManageBilling,
    onManageBilling,
    billingPortalLoading,
}) => {
    const enterprisePlan = currentPlanCode === "enterprise";
    const proPlan = currentPlanCode === "pro";
    const contactHref = buildMailtoHref(
        t("commercial.upgradeOptions.contactMail.subject", {
            defaultValue: "Verdictia — Capacité commerciale bloquée",
        }),
        t("commercial.upgradeOptions.contactMail.body", {
            defaultValue: "Bonjour,\n\nUne capacité est bloquée dans mon workspace Verdictia. Pouvez-vous m'indiquer l'action adaptée ?\n\nMerci.\n",
        })
    );

    return (
        <Alert
            type="info"
            showIcon
            message={t(`commercial.upgradeOptions.fallback.${currentPlanCode}.title`, {
                defaultValue: enterprisePlan
                    ? "Action à valider avec votre administrateur"
                    : proPlan
                    ? "Limite atteinte sur le plan actif"
                    : "Action indisponible avec le plan actif",
            })}
            description={(
                <Space direction="vertical" size={10}>
                    <Text>
                        {t(`commercial.upgradeOptions.fallback.${currentPlanCode}.body`, {
                            defaultValue: enterprisePlan
                                ? "Contactez votre administrateur ou l'équipe Verdictia pour ajuster les droits de votre workspace."
                                : proPlan
                                ? "Votre plan Pro est déjà le plus haut plan self-serve. Gérez la facturation ou contactez votre administrateur si cette limite doit être ajustée."
                                : "Aucune offre supérieure directe ne peut débloquer cette action automatiquement. Contactez votre administrateur ou l'équipe Verdictia.",
                        })}
                    </Text>
                    <Space wrap>
                        {canManageBilling && !enterprisePlan && (
                            <Button onClick={onManageBilling} loading={billingPortalLoading}>
                                {t("commercial.billing.manage", { defaultValue: "Gérer la facturation" })}
                            </Button>
                        )}
                        <Button href={contactHref}>
                            {enterprisePlan
                                ? t("commercial.upgradeOptions.contactAdminOrTeam", {
                                    defaultValue: "Contacter l'administrateur ou l'équipe",
                                })
                                : t("commercial.upgradeOptions.contactTeam", {
                                    defaultValue: "Nous contacter",
                                })}
                        </Button>
                    </Space>
                </Space>
            )}
        />
    );
};

const PlansReminder = ({
    t,
    onCheckout,
    loadingPlanCode,
    upgradeOptions,
    currentPlanCode,
    canManageBilling,
    onManageBilling,
    billingPortalLoading,
}) => {
    const plans = upgradeOptions
        .map((option) => {
            const definition = option.variant === "entrepreneur_repeat_purchase"
                ? ENTREPRENEUR_REPEAT_PURCHASE_PLAN_DEF
                : PLAN_DEFS_BY_CODE[option.planCode];
            if (!definition) return null;
            return {
                ...definition,
                ctaType: option.action === "contact" ? "contact" : definition.ctaType,
                ctaLabel: getUpgradeCtaLabel(option, t),
            };
        })
        .filter(Boolean);
    const hasSelfServePlans = plans.some((plan) => plan.ctaType !== "contact");
    const gridPlans = hasSelfServePlans
        ? plans.filter((plan) => plan.ctaType !== "contact")
        : plans;
    const contactPlans = hasSelfServePlans
        ? plans.filter((plan) => plan.ctaType === "contact")
        : [];
    const gridClassName = [
        "commercial-gate-plans__grid",
        gridPlans.length === 1 ? "commercial-gate-plans__grid--single" : null,
    ].filter(Boolean).join(" ");

    if (plans.length === 0) {
        return (
            <ContextualLimitAction
                currentPlanCode={currentPlanCode}
                t={t}
                canManageBilling={canManageBilling}
                onManageBilling={onManageBilling}
                billingPortalLoading={billingPortalLoading}
            />
        );
    }

    return (
        <div className="commercial-gate-plans">
            <div className="commercial-gate-plans__heading">
                <div>
                    <h3 className="commercial-gate-plans__title">
                        {t("commercial.upgradeOptions.heading", { defaultValue: "Options de déblocage" })}
                    </h3>
                    <p className="commercial-gate-plans__subtitle">
                        {t("commercial.upgradeOptions.subheading", {
                            defaultValue: "Verdictia affiche uniquement les offres supérieures pertinentes pour cette action.",
                        })}
                    </p>
                </div>
            </div>
            <div className={gridClassName}>
                {gridPlans.map((plan) => (
                    <PlanCard
                        key={plan.code}
                        plan={plan}
                        t={t}
                        onCheckout={onCheckout}
                        loadingPlanCode={loadingPlanCode}
                    />
                ))}
            </div>
            {contactPlans.map((plan) => (
                <EnterpriseContactStrip key={plan.code} plan={plan} t={t} />
            ))}
        </div>
    );
};

const CommercialGateModal = ({
    open,
    decision,
    currentPlan,
    usageSnapshot,
    canManageBilling = false,
    billingReturnPath = null,
    onClose,
    onConfirmPartial,
}) => {
    const { t, i18n } = useTranslation("review");
    const [loadingPlanCode, setLoadingPlanCode] = useState(null);
    const [billingPortalLoading, setBillingPortalLoading] = useState(false);
    const [billingError, setBillingError] = useState(null);
    const { title, body, ctaLabel } = getCommercialDecisionText(
        decision,
        t,
        i18n.resolvedLanguage || i18n.language
    );
    const partial = isPartialAnalysisDecision(decision);
    const isPageCountMismatch = decision?.code === "page_count_announced_mismatch";
    const ctaTarget = decision?.message?.cta_target;
    const resolvedCtaLabel = getCtaLabel(ctaTarget, ctaLabel, t);
    const { analyzedPages, excludedPages, announcedPages, detectedPages } = getPageCounts(decision);
    const resolvedCurrentPlan =
        currentPlan ||
        usageSnapshot?.plan ||
        decision?.usage_snapshot?.plan ||
        decision?.metadata_json?.plan ||
        null;
    const currentPlanCode = normalizeCommercialPlanCode(resolvedCurrentPlan);
    const decisionWithUsageContext = withEntrepreneurRepeatPurchaseContext(
        decision,
        currentPlanCode,
        usageSnapshot,
    );
    const upgradeOptions = getUpgradeOptions(currentPlanCode, decisionWithUsageContext);
    const showPlans = !partial && (ctaTarget === "upgrade" || ctaTarget === "contact");

    const handleCheckout = async (planCode) => {
        setBillingError(null);
        setLoadingPlanCode(planCode);
        try {
            await createCheckoutSession(planCode, { source: getAppBillingCheckoutSource() });
        } catch (error) {
            setBillingError(getBillingErrorMessage(error));
            setLoadingPlanCode(null);
        }
    };

    const handleBillingPortal = async () => {
        setBillingError(null);
        setBillingPortalLoading(true);
        try {
            await createCustomerPortalSession({ returnPath: billingReturnPath });
        } catch (error) {
            setBillingError(getBillingErrorMessage(error));
            setBillingPortalLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            centered
            destroyOnHidden
            title={null}
            footer={null}
            onCancel={onClose}
            maskClosable={!partial}
            width={showPlans ? (upgradeOptions.length > 1 ? 860 : 560) : 520}
            className="commercial-gate-modal"
        >
            <div className="commercial-gate-modal__inner">
                <div className="commercial-gate-modal__header">
                    <span className="commercial-gate-modal__eyebrow">
                        {partial
                            ? t("commercial.partial.eyebrow", { defaultValue: "Analyse partielle" })
                            : t("commercial.gate.eyebrow", { defaultValue: "Contrôle commercial" })}
                    </span>
                    <h2 className="commercial-gate-modal__title">{title}</h2>
                </div>

                <div className="commercial-gate-modal__body">
                    <p className="commercial-gate-modal__message">{body}</p>

                    {partial && (
                        <Alert
                            type="warning"
                            showIcon
                            message={t("commercial.partial.coverageTitle", { defaultValue: "Couverture de l'analyse" })}
                            description={(
                                <Space direction="vertical" size={4}>
                                    {analyzedPages !== null && (
                                        <Text>
                                            {t("commercial.partial.analyzedPages", {
                                                defaultValue: "Pages analysées : {{count}}",
                                                count: analyzedPages,
                                            })}
                                        </Text>
                                    )}
                                    {excludedPages !== null && (
                                        <Text>
                                            {t("commercial.partial.excludedPages", {
                                                defaultValue: "Pages exclues : {{count}}",
                                                count: excludedPages,
                                            })}
                                        </Text>
                                    )}
                                    {isPageCountMismatch && announcedPages !== null && (
                                        <Text>
                                            {t("commercial.pageCountMismatch.announcedPages", {
                                                defaultValue: "Pages annoncées par le PDF : {{count}}",
                                                count: announcedPages,
                                            })}
                                        </Text>
                                    )}
                                    {isPageCountMismatch && detectedPages !== null && (
                                        <Text>
                                            {t("commercial.pageCountMismatch.detectedPages", {
                                                defaultValue: "Pages détectées : {{count}}",
                                                count: detectedPages,
                                            })}
                                        </Text>
                                    )}
                                </Space>
                            )}
                        />
                    )}

                    {billingError && (
                        <Alert
                            type="error"
                            showIcon
                            message={billingError}
                        />
                    )}

                    {showPlans && (
                        <PlansReminder
                            t={t}
                            onCheckout={handleCheckout}
                            loadingPlanCode={loadingPlanCode}
                            upgradeOptions={upgradeOptions}
                            currentPlanCode={currentPlanCode}
                            canManageBilling={canManageBilling}
                            onManageBilling={handleBillingPortal}
                            billingPortalLoading={billingPortalLoading}
                        />
                    )}
                </div>

                <div className="commercial-gate-modal__footer">
                    {resolvedCtaLabel && !partial && !showPlans && ctaTarget !== "close" && (
                        <Button onClick={onClose}>
                            {resolvedCtaLabel}
                        </Button>
                    )}
                    <Button onClick={onClose}>
                        {t("commercial.cta.close", { defaultValue: "Fermer" })}
                    </Button>
                    {partial && (
                        <Button type="primary" onClick={onConfirmPartial}>
                            {isPageCountMismatch
                                ? t("commercial.pageCountMismatch.confirm", { defaultValue: "Continuer l'analyse" })
                                : t("commercial.partial.confirm", { defaultValue: "Confirmer l'analyse partielle" })}
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default CommercialGateModal;
